if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.buyOnceButton = this.querySelector('[data-buy-once]');
        this.loadingSpinner = this.querySelector('.loading__spinner');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.addToCartAction = this.dataset.addToCartAction || 'cart_drawer';
        this.buyOnceButton?.addEventListener('click', this.onBuyOnceHandler.bind(this));
      }

      suppressRechargeStorefront() {
        document
          .querySelectorAll('#recharge-storefront-experiences, [id^="recharge-storefront-experiences"]')
          .forEach((element) => {
            element.setAttribute('hidden', '');
            element.setAttribute('aria-hidden', 'true');
            element.style.setProperty('display', 'none', 'important');
            element.style.setProperty('visibility', 'hidden', 'important');
            element.style.setProperty('pointer-events', 'none', 'important');
          });
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.isLoading()) return;
        this.submitCartRequest({ includeSellingPlan: true, triggerEvent: evt, loadingTarget: 'submit' });
      }

      onBuyOnceHandler(evt) {
        evt.preventDefault();
        if (this.isLoading()) return;
        this.submitCartRequest({ includeSellingPlan: false, triggerEvent: evt, loadingTarget: 'buyOnce' });
      }

      isLoading() {
        return (
          this.submitButton.getAttribute('aria-disabled') === 'true' ||
          this.buyOnceButton?.getAttribute('aria-disabled') === 'true'
        );
      }

      setRequestLoading(isLoading, loadingTarget = 'submit') {
        if (loadingTarget === 'buyOnce' && this.buyOnceButton) {
          this.buyOnceButton.toggleAttribute('aria-disabled', isLoading);
          this.buyOnceButton.classList.toggle('loading', isLoading);
          return;
        }

        this.submitButton.toggleAttribute('aria-disabled', isLoading);
        this.submitButton.classList.toggle('loading', isLoading);
        this.loadingSpinner?.classList.toggle('hidden', !isLoading);
      }

      submitCartRequest({ includeSellingPlan, triggerEvent, loadingTarget = 'submit' }) {
        this.handleErrorMessage();
        this.setRequestLoading(true, loadingTarget);

        const formData = new FormData(this.form);
        if (!includeSellingPlan) {
          formData.delete('selling_plan');
        }

        const cartItems = this.buildCartItems(formData, includeSellingPlan);
        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const redirectToCheckout = this.addToCartAction === 'checkout';
        const linesUpdateDeferred = redirectToCheckout ? null : this.createCartLinesUpdateEvent(cartItems);

        if (redirectToCheckout) {
          this.suppressRechargeStorefront();
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.body = JSON.stringify(this.buildCartAddPayload(cartItems, redirectToCheckout));

        if (this.cart && !redirectToCheckout) {
          this.cart.setActiveElement(document.activeElement);
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.setRequestLoading(true, 'submit');
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            if (redirectToCheckout) {
              window.location.assign(window.routes?.checkout_url || '/checkout');
              return;
            }

            if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            }

            this.error = false;

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure('add:paint-updated-sections', () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure('add:paint-updated-sections', () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            if (redirectToCheckout) return;
            if (!this.error) this.setRequestLoading(false, loadingTarget);
            if (this.cart?.classList.contains('is-empty')) this.cart.classList.remove('is-empty');

            if (triggerEvent) {
              CartPerformance.measureFromEvent('add:user-action', triggerEvent);
            }
          });
      }

      buildCartItems(formData, includeSellingPlan) {
        const variantId = parseInt(formData.get('id'), 10);
        const quantity = parseInt(formData.get('quantity'), 10) || 1;
        const properties = this.getFormProperties(formData);

        const mainItem = { id: variantId, quantity };
        if (properties) mainItem.properties = properties;

        if (includeSellingPlan) {
          const sellingPlan = formData.get('selling_plan');
          if (sellingPlan) mainItem.selling_plan = parseInt(sellingPlan, 10);
        }

        const items = [mainItem];

        if (includeSellingPlan) {
          const subscriptionPurchase = document.querySelector(
            `subscription-purchase[data-section-id="${this.dataset.sectionId}"]`
          );
          const giftItems = subscriptionPurchase?.getUnlockedGiftCartItems?.(variantId) || [];
          items.push(...giftItems);
        }

        return items;
      }

      buildCartAddPayload(items, skipSections = false) {
        const payload = { items };

        if (this.cart && !skipSections) {
          payload.sections = this.cart.getSectionsToRender().map((section) => section.id).join(',');
          payload.sections_url = window.location.pathname;
        }

        return payload;
      }

      getFormProperties(formData) {
        const properties = {};

        formData.forEach((value, key) => {
          const match = key.match(/^properties\[(.+)\]$/);
          if (match && value !== '') properties[match[1]] = value;
        });

        return Object.keys(properties).length ? properties : null;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(items) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: items.map((item) => ({
              merchandiseId: String(item.id),
              quantity: item.quantity,
            })),
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return Promise.resolve();

        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return Promise.resolve();

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        return pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) {
              deferred.reject(new Error('Missing currency in cart response'));
              return;
            }
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
